import type { FC, PropsWithChildren } from 'react'
import styled from '@emotion/styled'
import { m } from 'framer-motion'

import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'
import ColumnTrack from '@segment/matcha/components/ColumnTrack'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import EnsureCustomerDataImage from './EnsureCustomerDataImage'

const copy = {
  overline: `Twilio Segment EU regional infrastructure`,
  heading: `Help enable compliance with GDPR`,
}

const boxes = [
  {
    headline: `Ingest customer data in the EU`,
    content: `Collect EU customer data in the EU at the click of a button with no advanced configuration required.`,
  },
  {
    headline: `Process customer data in the EU`,
    content: `Ensure EU customer data is processed, filtered, validated, and deduplicated on infrastructure hosted in the EU.`,
  },
  {
    headline: `Store customer data in the EU`,
    content: `Reduce long-term data exposure by storing EU customer data in Segment-hosted archives in the EU.`,
  },
  {
    headline: `Personalize every interaction without sacrificing your data etiquette`,
    content: `Enrich user profiles, build real-time audiences and journeys, create a complete view of the customer, and activate audiences on infrastructure hosted in the EU.`,
  },
]

const EnsureCustomerData: FC<PropsWithChildren<unknown>> = () => {
  return (
    <SectionBase variant="2Col" reverse={true} copyVariant="narrow" sticky={true}>
      <Section.Copy>
        <CopyTextBase>
          <CopyText.Overline tag="p" variant="h5">
            {copy.overline}
          </CopyText.Overline>
          <CopyText.Heading tag="h2" variant="h2">
            {copy.heading}
          </CopyText.Heading>
        </CopyTextBase>
        <ColumnTrackWrapper>
          <ColumnTrack gap="medium">
            {boxes.map(({ headline, content }) => (
              <FeatureBoxBase alignment="left" key={headline}>
                <FeatureBox.Body>
                  <FeatureBox.Title>{headline}</FeatureBox.Title>
                  <FeatureBox.Text>{content}</FeatureBox.Text>
                </FeatureBox.Body>
              </FeatureBoxBase>
            ))}
          </ColumnTrack>
        </ColumnTrackWrapper>
      </Section.Copy>
      <Section.Contents>
        <Section.Sticky>
          <ImageWrapper
            initial={{ opacity: 0 }}
            whileInView={{
              opacity: 1,
              transition: {
                duration: 0.4,
                delay: 0.1,
              },
            }}
            viewport={{ once: true }}
          >
            <EnsureCustomerDataImage />
          </ImageWrapper>
        </Section.Sticky>
      </Section.Contents>
    </SectionBase>
  )
}

export default EnsureCustomerData

const ColumnTrackWrapper = styled.div`
  margin-top: 40px;
`

const ImageWrapper = styled(m.div)``
